import { ReactElement, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { CreatePostDraftResponse, PostListItem, useCategoriesListQuery } from 'api';
import {
  Backdrop,
  BaseTextFieldController,
  Form,
  FormGroup,
  MediaUploadZone,
  SelectController,
  FormAttachments,
  RichEditorController,
  NewConfirmDialog,
} from 'components';
import { IMAGE_MIME_TYPES, VIDEO_MIME_TYPES } from 'components/MediaUploadZone/constants';
import { multiSelectStyles } from 'features/documentCategories/components/CreateDocumentCategoriesForm/styles';
import { useWindowSize, useAuth } from 'hooks';
import { textFieldMobileStyles, textFieldStyles, richEditorStyles } from 'styles';
import { UserRole } from 'types';

import { MAX_ARTICLE_LENGTH, MAX_TITLE_LENGTH, AllOption } from './constants';
import { useCreateDocumentForm } from './hooks';
import PlaceSelect from './placeSelect';
import { descriptionFieldRules, titleFieldRules } from './rules';
import {
  CancelBtn,
  CreateBtn,
  Footer,
  FormButtonsWrapper,
  FromContainer,
  PublicDocumentNote,
} from './styled';
import { CheckboxContainer, InputW, LabelW, LabelTitle, CheckboxSection } from './styles';

interface CreatePostFormProps {
  draft?: CreatePostDraftResponse;
  document?: PostListItem;
  isEdit: boolean;
  type?: string;
  category?: string;
  onClose?: () => void;
}

export function CreateDocumentForm({
  draft,
  document,
  isEdit,
  type,
  category,
  onClose,
}: CreatePostFormProps): ReactElement {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { employeeId = '' } = useParams();
  const { user, isManager } = useAuth();
  const { isMobile } = useWindowSize();
  const {
    control,
    handleFormSubmit,
    handleAddImages,
    handleAddDocuments,
    handleAddVideos,
    attachedImages,
    attachedDocs,
    attachedVideos,
    handleDeleteAttachment,
    isLoading,
    isOpen,
    handleCloseModal,
    selectedPlaces,
    setSelectedPlaces,
    setFirstChange,
    handleAssigneeChange,
    handleCheckboxChange,
    handlePinCheckboxChange,
    handleAssigneeDefault,
    notification,
    pin,
    usersList,
    isPublicDocument,
    buttonDisable,
    categoriesOptionsList,
    categoryMap,
    progress,
    handleSubmitConfirmModal,
    handleCloseButton,
    handleSelectGalleryFiles,
  } = useCreateDocumentForm({
    draft,
    document,
    isEdit,
    onClose,
  });
  const visibility = true;
  const { categoriesList } = useCategoriesListQuery(visibility);

  const resolvedCategoryIds = useMemo(() => {
    const categories: string[] = [];

    const isNewsfeedExist = selectedPlaces.find(
      (sp) => sp.group === t('post.create.form.placeGroups.newsfeed'),
    );
    const isProfileExist = selectedPlaces.find(
      (sp) => sp.group === t('post.create.form.placeGroups.profiles'),
    );
    selectedPlaces.forEach((sp) => {
      if (sp.group === t('post.create.form.placeGroups.categories')) {
        categories.push(sp.code);
      }
    });

    if (isNewsfeedExist) {
      const newsfeed = categoriesList?.find(
        (c) => c.title === t('post.create.form.placeGroups.newsfeed'),
      );
      if (newsfeed?.id) {
        categories.push(newsfeed.id);
      }
    }

    if (isProfileExist) {
      const profile = categoriesList?.find(
        (c) => c.title === t('post.create.form.placeGroups.profiles'),
      );
      if (profile?.id) {
        categories.push(profile.id);
      }
    }

    return categories;
  }, [selectedPlaces, t, categoriesList]);

  const handleUploadAll = async (files: FileList) => {
    const toFileList = (arr: File[]): FileList => {
      const dt = new DataTransfer();
      arr.forEach((f) => dt.items.add(f));
      return dt.files;
    };
    const imageFiles = Array.from(files).filter((f) => IMAGE_MIME_TYPES.includes(f.type));
    const videoFiles = Array.from(files).filter((f) => VIDEO_MIME_TYPES.includes(f.type));
    const docFiles = Array.from(files).filter(
      (f) => !IMAGE_MIME_TYPES.includes(f.type) && !VIDEO_MIME_TYPES.includes(f.type),
    );

    const post_id = draft?.id || document?.id;
    if (imageFiles.length) await handleAddImages(toFileList(imageFiles), [], post_id);
    if (videoFiles.length) await handleAddVideos(toFileList(videoFiles), [], post_id);
    if (docFiles.length) await handleAddDocuments(toFileList(docFiles), [], post_id);
  };

  const profileUsers = usersList.map((user) => {
    return {
      code: user?.id,
      label: `${user?.firstName} ${user?.lastName}`,
      group: t('post.create.form.placeGroups.profiles'),
      isManager: user.role === UserRole.Manager,
    };
  });

  const formGroupMarginBottom = isMobile ? 8 : 24;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;

  const employeeUser = usersList.find((i) => i.id === employeeId);

  const allCategoriesList = useMemo(
    () => [
      {
        code: 'newsfeed',
        label: t('post.create.form.placeGroups.newsfeed'),
        group: t('post.create.form.placeGroups.newsfeed'),
      },
      ...categoriesOptionsList.map((value) => ({
        code: value.value,
        label: value.label,
        group: t('post.create.form.placeGroups.categories'),
      })),
      ...profileUsers,
    ],
    [categoriesOptionsList, profileUsers, t],
  );

  const checkPlace = selectedPlaces?.filter(
    (item) => item?.group === t('post.create.form.placeGroups.categories'),
  );
  const checkProfile = selectedPlaces?.some(
    (item) => item?.group === t('post.create.form.placeGroups.profiles'),
  );

  const usersOptions = usersList.map((user) => ({
    value: user.id,
    role: user.role,
    label: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email,
  }));

  const visibilityOptions = [AllOption, ...usersOptions];

  useEffect(() => {
    if (!isEdit) {
      if (type === 'post' && (pathname === '/we/add-post' || pathname.includes('actions'))) {
        setSelectedPlaces([
          {
            code: 'newsfeed',
            label: t('post.create.form.placeGroups.newsfeed'),
            group: t('post.create.form.placeGroups.newsfeed'),
          },
        ]);
        handleAssigneeDefault('all');
      } else if (type === 'category' && category) {
        setSelectedPlaces([
          {
            code: categoryMap?.[category.toLowerCase()]?.id,
            label: category,
            group: t('post.create.form.placeGroups.categories'),
          },
        ]);
      } else if (type === 'profile' && !category && !employeeId) {
        setSelectedPlaces([
          {
            code: user?.id || '',
            label: `${user?.first_name} ${user?.last_name}`,
            group: t('post.create.form.placeGroups.profiles'),
          },
        ]);
        handleAssigneeDefault(user?.id || '');
      } else if (type === 'profile' && employeeId && user?.id && !category) {
        if (employeeUser?.id && user?.id !== employeeUser?.id) {
          setSelectedPlaces([
            {
              code: user?.id || '',
              label: `${user?.first_name} ${user?.last_name}`,
              group: t('post.create.form.placeGroups.profiles'),
            },
            {
              code: employeeUser?.id || '',
              label: `${employeeUser?.firstName} ${employeeUser?.lastName}`,
              group: t('post.create.form.placeGroups.profiles'),
            },
          ]);
        } else {
          setSelectedPlaces([
            {
              code: user?.id || '',
              label: `${user?.first_name} ${user?.last_name}`,
              group: t('post.create.form.placeGroups.profiles'),
            },
          ]);
        }
      }
    }
  }, [
    setSelectedPlaces,
    isEdit,
    type,
    handleAssigneeDefault,
    category,
    pathname,
    employeeId,
    user?.id,
    user?.first_name,
    user?.last_name,
    t,
    categoryMap,
    employeeUser?.id,
    employeeUser?.firstName,
    employeeUser?.lastName,
  ]);

  return (
    <>
      {isLoading && <Backdrop progress={progress} />}
      <FromContainer>
        <Form onSubmit={handleFormSubmit}>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('post.create.form.placeholders.title')}</LabelTitle>
            <BaseTextFieldController
              name="title"
              control={control}
              placeholder={t('document.create.form.placeholders.title')}
              sx={fieldStyles}
              maxLength={MAX_TITLE_LENGTH}
              rules={titleFieldRules}
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('post.create.form.placeholders.description')}</LabelTitle>
            <RichEditorController
              name="description"
              control={control}
              placeholder={t('document.create.form.placeholders.description')}
              maxLength={MAX_ARTICLE_LENGTH}
              rules={descriptionFieldRules}
              sx={richEditorStyles(isMobile)}
            />
          </FormGroup>
          <LabelTitle>{t('post.create.form.placeholders.place')}</LabelTitle>
          <PlaceSelect
            allUserCategoriesList={allCategoriesList}
            selectedPlaces={selectedPlaces}
            setSelectedPlaces={setSelectedPlaces}
            setFirstChange={setFirstChange}
          />
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('post.create.form.placeholders.who')}</LabelTitle>
            <SelectController
              name="visibility"
              control={control}
              placeholder={t('post.create.form.placeholders.who')}
              options={visibilityOptions}
              multipleSelect
              sx={multiSelectStyles(isMobile)}
              handleChange={handleAssigneeChange}
              type="poll"
              disableDropdown={!!checkPlace?.length && !checkProfile}
              categoryLength={checkPlace?.length || 0}
            />
          </FormGroup>

          <FormAttachments
            attachedImages={attachedImages}
            attachedDocs={attachedDocs}
            attachedVideos={attachedVideos}
            handleDeleteAttachment={handleDeleteAttachment}
            draftId={draft?.id || document?.id || ''}
            hasDownloadDoc={isEdit}
          />

          <MediaUploadZone
            onUpload={handleUploadAll}
            formType="createDocument"
            categoryIds={resolvedCategoryIds}
            onSelectFromGallery={handleSelectGalleryFiles}
          />

          {isMobile && !isEdit && (
            <PublicDocumentNote>
              {isPublicDocument && t('document.create.form.autogeneratedPostNote')}
            </PublicDocumentNote>
          )}
          <CheckboxSection>
            {isManager && (
              <CheckboxContainer>
                <InputW
                  type="checkbox"
                  name="pin"
                  onChange={handlePinCheckboxChange}
                  checked={pin}
                />
                <LabelW>{t('post.create.form.placeholders.pin')}</LabelW>
              </CheckboxContainer>
            )}
            <CheckboxContainer>
              <InputW
                type="checkbox"
                name="notification"
                onChange={handleCheckboxChange}
                checked={notification}
              />
              <LabelW>{t('post.create.form.placeholders.notification')}</LabelW>
            </CheckboxContainer>
          </CheckboxSection>
          {!isMobile && (
            <Footer>
              <PublicDocumentNote>
                {isPublicDocument && !isEdit && t('document.create.form.autogeneratedPostNote')}
              </PublicDocumentNote>
              <FormButtonsWrapper>
                <CancelBtn variant="outlined" onClick={handleCloseButton}>
                  {t('document.create.form.buttons.cancel')}
                </CancelBtn>
                <CreateBtn variant="contained" type="submit" disabled={buttonDisable}>
                  {isEdit
                    ? t('document.create.form.buttons.edit')
                    : t('document.create.form.buttons.create')}
                </CreateBtn>
              </FormButtonsWrapper>
            </Footer>
          )}
          {isMobile && (
            <FormButtonsWrapper
              className="fixed-bottom"
              sx={{ paddingTop: '16px', background: 'white' }}
            >
              <CancelBtn variant="outlined" onClick={handleCloseButton}>
                {t('document.create.form.buttons.cancel')}
              </CancelBtn>
              <CreateBtn variant="contained" disabled={buttonDisable} onClick={handleFormSubmit}>
                {isEdit
                  ? t('document.create.form.buttons.edit')
                  : t('document.create.form.buttons.create')}
              </CreateBtn>
            </FormButtonsWrapper>
          )}
          <NewConfirmDialog
            title={t('document.create.modal.title')}
            subTitle={t('document.create.modal.subtitle')}
            open={isOpen}
            onClose={handleCloseModal}
            onConfirm={handleSubmitConfirmModal}
            submitLabel={t('document.create.modal.successButton')}
            cancelLabel={t('document.create.modal.cancelButton')}
          />
        </Form>
      </FromContainer>
    </>
  );
}
